/*
 * Copyright 2026 Sony Corporation
 */

import {
  CreativeLookModeSchema,
  type CreativeLook,
  type CreativeLookMode,
} from '@ips-device-verse/twin-service/schema';

export type CreativeLookField = keyof CreativeLook;

export interface SliderField {
  key: Exclude<CreativeLookField, 'mode'>;
  label: string;
  min: number;
  max: number;
}

/**
 * The eight numeric adjustments, in the order the device's own menu lists them.
 *
 * Ranges are the device's, mirrored from `CreativeLookSchema`. They are repeated
 * rather than derived because a zod schema does not expose its bounds, and a
 * slider that disagrees with the schema produces a write the API rejects.
 */
export const CREATIVE_LOOK_SLIDERS: readonly SliderField[] = [
  { key: 'contrast', label: 'Contrast', min: -9, max: 9 },
  { key: 'highlights', label: 'Highlights', min: -9, max: 9 },
  { key: 'shadows', label: 'Shadows', min: -9, max: 9 },
  { key: 'fade', label: 'Fade', min: 0, max: 9 },
  { key: 'saturation', label: 'Saturation', min: -9, max: 9 },
  { key: 'sharpness', label: 'Sharpness', min: 0, max: 9 },
  { key: 'sharpnessRange', label: 'Sharpness range', min: 1, max: 5 },
  { key: 'clarity', label: 'Clarity', min: 0, max: 9 },
];

export const CREATIVE_LOOK_MODES: readonly CreativeLookMode[] = CreativeLookModeSchema.options;

export const CREATIVE_LOOK_FIELDS: readonly CreativeLookField[] = [
  'mode',
  ...CREATIVE_LOOK_SLIDERS.map((slider) => slider.key),
];

/** What a look with nothing set looks like: the device's own defaults. */
export const NEUTRAL: Required<CreativeLook> = {
  mode: 'ST',
  contrast: 0,
  highlights: 0,
  shadows: 0,
  fade: 0,
  saturation: 0,
  sharpness: 0,
  sharpnessRange: 3,
  clarity: 0,
};
